<template>
	<app-page v-loading="dashboardsLoading || dashboardLoading" :class="themeCssClass">
        <section v-if="isTVActive" :style="pixelShiftingStyles">
            <div class="dashboard-breadcrumbs">
                <div
                    v-for="dashboard in tvDashboards"
                    :key="dashboard.dashboardID"
                    :class="{ active: activeDashboard.dashboardID === dashboard.dashboardID }"
                    class="dashboard-breadcrumb-item"
                    @click="setActiveDashboard(dashboard)"
                >
                    <tooltip
                        :title="dashboard.dashboardName"
                        :delay-in="0"
                        :offset-y="24"
                    />
                </div>
            </div>

            <header v-if="activeDashboard" ref="header" class="tv-header">
                <div class="title">
                    <h1>{{activeDashboard.dashboardName}}</h1>
                    <h2>{{dateRangeDisplay}}</h2>
                </div>
                <btn
                    icon="close"
                    title="Exit TV mode"
                    class="btn-tv-close"
                    @click="end"
                />
            </header>

		    <section v-if="activeDashboard">
			    <section v-if="dashboardPane == 0" class="px-1 pb-1" style="position: relative;">
				    <dashboard
                        ref="dashboard"
                        :id="activeDashboard.dashboardID"
                        :style="dashboardStyles"
                        :readonly="true"
                        @loading="dashboardLoading = true"
                        @loaded="dashboardLoading = false"
                    /> 
			    </section>
                <div v-if="dashboardPane == 1" class="px-1 pb-1" style="position: relative;">
				    <dashboard
                        ref="dashboard"
                        :id="activeDashboard.dashboardID"
                        :style="dashboardStyles"
                        :readonly="true"
                        @loading="dashboardLoading = true"
                        @loaded="dashboardLoading = false"
                    /> 
			    </div>
		    </section>
        </section>

        <start-tv-modal
            ref="startTVModal"
            @start="onStartTV"
            @cancel="onCancelTV"
            @dismiss="onCancelTV"
        />
	</app-page>
</template>


<script>
    import _ from 'lodash';
    import moment from 'moment';
    import BigScreen from 'bigscreen';
    import { AsyncMixin } from 'pdk/mixins';
    import { browser } from 'pdk/services';
    import AppConfig from '@/config';
    import { actions, getters } from 'dashboard-pdk/store';
    import { actions as appStoreActions, getters as appStoreGetters } from '@/store';
    import { account } from '@/services';
    import themes from 'dashboard-pdk/themes';
    import { Dashboard } from 'dashboard-pdk/components/dashboards';
    import { StartTvModal } from './modals';


    export default {
        name: 'dashboard-tv-page',
		components: {
            Dashboard,
            StartTvModal
        },
        mixins: [AsyncMixin],
        provide() {
            const result = {};
            
            Object.defineProperty(result, 'theme', {
                enumerable: true,
                get: () => this.selectedTheme,
                set: (val) => { this.selectedTheme = val; }
            });

            return { dashboardConfig: result };
        },
        beforeRouteEnter(_to, _from, next) {
            account.getThemePreference().then((preferences) => {
                next((vm) => {
                    vm.theme = preferences.theme || 'light';
                });
            });
        },
		data() {
            return {
                dashboardLoading: false,
                relativeDateRangeUpdaterHandler: null,
                isFullscreenActive: false,
                theme: null,
                themes,
                keepAliveHandler: undefined,
                rotationHandler: undefined,
                pixelShiftingHandler: undefined,
                dashboardPane: 0,
                pixelShiftingStyles: {
                    position: 'relative',
                    'transition-timing-function': 'ease',
                    'transition-duration': '1000ms',
                    'transition-property': 'top left',
                    'will-change': 'top left',
                    top: 0,
                    left: 0
                }
			};
		},
        computed: {
            themeCssClass() {
                return `dashboard-theme-${this.theme}`;
            },
            selectedTheme() {
                return _.find(this.themes, (t) => t.id === this.theme);
            },
            dashboards() {
				return this.$store.state.dashboard.dashboards;
            },
			tvDashboards() {
				return this.$store.getters[getters.TV_DASHBOARDS];
            },
            widgets() {
				return this.$store.getters[getters.WIDGETS]();
            },
            dashboardsLoading() {
                return typeof this.$store.state.dashboard.dashboards === 'undefined';
            },
			activeDashboard() {
				return this.$store.getters[getters.ACTIVE_DASHBOARD];
            },
            startDateFilter() {
                if (!this.activeDashboard) return undefined;

                return this.activeDashboard.startDate;
			},
			endDateFilter() {
				if (!this.activeDashboard) return undefined;

                return this.activeDashboard.endDate;
			},
            dateRangeDisplay() {
                if (!this.activeDashboard) return undefined;

				const format = 'M/D/YYYY';
				const start = moment(this.startDateFilter);
                const end = moment(this.endDateFilter);
                const isRelative = this.activeDashboard.dateRange == 9; // Custom range…

				if (start.isSame(end, 'day') && start.isSame(end, 'month') && start.isSame(end, 'year'))
					return start.format(format);
				

				if(isRelative) {
					const startOfMonth = start.clone().startOf('month');
					const endOfMonth = end.clone().endOf('month');

					if(start.isSame(end, 'year') && start.isSame(end, 'month') && start.isSame(startOfMonth, 'day') && end.isSame(endOfMonth, 'day'))
						return start.format('MMMM YYYY');
				}

				return `${start.format(format)} - ${end.format(format)}`;
            },
            isRelativeDateRange() {
                if (!this.activeDashboard) return false;

                return this.activeDashboard.dateRange != 9; // Custom range…
            },
            dashboardWidth() {
                if (!this.widgets) return 'auto';

                const width = _.max(_.map(this.widgets, (w) => w.x + w.width));
                return width;
            },
            formattedDashboardWidth() {
                if (this.dashboardWidth == 'auto') return this.dashboardWidth;
                return `${this.dashboardWidth}px`;
            },
            dashboardHeight() {
                if (!this.widgets) return 'auto';

                const height = _.max(_.map(this.widgets, (w) => w.y + w.height));
                return height;
            },
            formattedDashboardHeight() {
                if (this.dashboardHeight == 'auto') return this.dashboardHeight;
                return `${this.dashboardHeight}px`;
            },
            dashboardTransform() {
                if (!this.widgets) return 'none';

                const header = this.$refs.header;
                if (header) {

                    // Calculate our object's width and heights
                    const viewport = browser.getViewport();
                    viewport.width -= 32; // Add some padding on the sides
                    viewport.height -= header.clientHeight + 16; // Account some padding on the bottom

                    const dashboard = {
                        width: this.dashboardWidth,
                        height: this.dashboardHeight
                    };

                    // Determine what our scale should be to ensure we are fitting everything nicely in the viewport
                    let scale = 1;

                    const ratioW = dashboard.width / viewport.width;
                    const ratioH = dashboard.height / (viewport.height - 20);

                    const ratio = _.max([ratioW, ratioH]);
                    scale = ((1 - ratio) / ratio) + 1;

                    return `scale(${scale})`;
                }

                return 'none';
            },
            dashboardRotationInterval() {
                return this.$store.getters[getters.TV_DASHBOARD_PREFERENCES].tvDashboardRotationInterval;
            },
            isTVActive() {
                return this.$store.state.dashboard.tv.active;
            },
            dashboardStyles() {
                const styles = {
                    width: this.formattedDashboardWidth,
                    height: this.formattedDashboardHeight,
                    position: 'absolute',
                    left: '50%',
                    'margin-left': `-${this.dashboardWidth / 2}px`,
                    'transform-origin': 'top center',
                    transform: this.dashboardTransform
                };

                return styles;
            },
            appUpdateRequired() {
                return this.$store.getters[appStoreGetters.APP_UPDATE_REQUIRED];
            }
        },
        watch: {
            appUpdateRequired(value) {
                if (value) {
                    this.$store.dispatch(actions.WIDGETS_CLEAR);
                    this.endRotation();
                    this.endRelativeDateRangeUpdater();
                }
            }
        },
        mounted() {
            if (this.isTVActive === false) {
                setTimeout(() => {
                    this.$refs.startTVModal.open();
                }, 0);
            }
            else {
                this.start();
            }
        },
        beforeDestroy() {
            this.endRotation();
            this.endFullscreen();
            this.endKeepAlive();
            this.endRelativeDateRangeUpdater();
            this.endPixelShifting();

            this.$store.dispatch(actions.TV_DEACTIVATE);
        },
        methods: {

            // Master functions
            start() {
                this.isFullscreenActive = true;

                // Fullscreen event handlers
                BigScreen.onenter = () => {
                    this.isFullscreenActive = true;
                    document.getElementsByTagName('body')[0].classList.add('fullscreen');
                };
                BigScreen.onexit = () => {
                    this.isFullscreenActive = false;
                    document.getElementsByTagName('body')[0].classList.remove('fullscreen');
                    this.end();
                };

                // Start the processes
                this.startKeepAlive();
                this.startRotation();
                this.startRelativeDateRangeUpdater();
                this.startPixelShifting();
            },
            end() {
                // beforeDestroy() handles much of our cleanup
                this.$router.replace({ name: 'dashboards' });
            },

            // Start TV Modal
            onStartTV() {
                this.start();
            },
            onCancelTV() {
                this.end();
            },

            // Keep-alive
            startKeepAlive() {
                //token lifetime minus refreshThreshold converted to milliseconds 
                const refreshInterval = (this.$auth.tokenParsed.exp - this.$auth.tokenParsed.iat - AppConfig.auth.refreshThreshold)*1000;
                this.endKeepAlive();

                this.keepAliveHandler = setInterval(() => {
                    this.$auth.updateToken(-1)
                        .then((refreshed) => {
                            if (!refreshed)
                                this.$auth.clearToken();
                        }).catch(() => {
                            this.$auth.clearToken();
                        });
                }, refreshInterval);
            },
            endKeepAlive() {
                if (this.keepAliveHandler) {
                    clearInterval(this.keepAliveHandler);
                    this.keepAliveHandler = undefined;
                }
            },

            // Rotation
            endRotation() {
                if (this.rotationHandler) {
                    clearInterval(this.rotationHandler);
                    this.rotationHandler = undefined;
                }
            },
            startRotation() {
                this.endRotation();

                this.rotationHandler = setInterval(() => {
                    this.advanceRotation();
                }, this.dashboardRotationInterval);
            },
            advanceRotation() {
                const activeDashboardID = this.$store.getters[getters.ACTIVE_DASHBOARD_ID]();
                const dashboardIDs = _.map(this.$store.getters[getters.TV_DASHBOARDS], (d) => d.dashboardID);
                const index = dashboardIDs.indexOf(activeDashboardID);

                // This is an untraditional approach to say the least, but we're doing this to help the browser's GC run more often.
                // By toggling the DOM objects used, GC runs more often and sweeps up more objects.
                // We only need to do this approach on single-dashboard rotations.
                // NOTE: the only reason this workaround is necessary is because we are now refreshing the entire dashboard and all widgets
                // on renew, not just the widget's data. This allows newly-added widgets to appear while in TV mode.

                // If we only have one dashboard in the rotation, renew it.
                if (dashboardIDs.length === 1) {
                    this.dashboardPane = this.dashboardPane == 0 ? 1 : 0;
                    this.$refs.dashboard.renew();
                    return;
                }

                const newPaneValue = this.dashboardPane == 0 ? 1 : 0;
                this.dashboardPane = -1;

                let newActiveDashboardID;
                if (index === dashboardIDs.length - 1) newActiveDashboardID = dashboardIDs[0];
                else newActiveDashboardID = dashboardIDs[index + 1];

                this.$store.dispatch(actions.DASHBOARD_ACTIVATE, { dashboardID: newActiveDashboardID })
                    .then(() => {
                        this.dashboardPane = newPaneValue;
                    });
            },

            // Dashboards
            setActiveDashboard(dashboard) {
                this.$store.dispatch(actions.DASHBOARD_ACTIVATE, {
					dashboardID: dashboard.dashboardID
                });

                this.startRotation();
            },

            // Date Ranges
            startRelativeDateRangeUpdater() {
                /*
			    * Start the relative date range updater when the next minute starts.
			    * This will ensure that the clock on the local machine and their dashboard are in sync
			    * when checking for relative date ranges by the minute.
			    */
			    const now = moment();
			    const start = moment().endOf('minute');
			    const ms = start.diff(now, 'milliseconds');
			    setTimeout(() => {
				    this.initializeRelativeDateRangeUpdater();
                }, ms);
			},
            initializeRelativeDateRangeUpdater() {
                /*
			     * Keeps the date range picker up-to-date in case the selected range is a relative one
			     * and the dashboard is open when midnight hits.
			     */

				var lastDateChecked = moment();

				this.endRelativeDateRangeUpdater();

                this.relativeDateRangeUpdaterHandler = setInterval(() => {
                    if (!this.isRelativeDateRange) return;

					const now = moment();
					const hasDayChanged = !now.isSame(lastDateChecked, 'day');
					lastDateChecked = now.clone();

					if (hasDayChanged) {
                        this.setFilterDateRange(
                            this.activeDashboard.dateRange,
                            moment(this.activeDashboard.startDate).add(1, 'days').format(),
                            moment(this.activeDashboard.endDate).add(1, 'days').format(),
                            this.activeDashboard.groupBy
                        );
                    }					
				}, 60000);
            },
			endRelativeDateRangeUpdater() {
                if (this.relativeDateRangeUpdaterHandler) {
                    clearInterval(this.relativeDateRangeUpdaterHandler);
                }
                this.relativeDateRangeUpdaterHandler = undefined;
			},
            setFilterDateRange(dateRange, startDate, endDate, groupBy) {
                this.$store.dispatch(actions.DASHBOARD_DATE_RANGE_SET, {
                    dashboardID: this.activeDashboard.dashboardID,
                    dateRange,
					startDate,
					endDate,
					groupBy
				});
            },

            // Pixel shifting (for burn-in protection)
            startPixelShifting() {
                this.endPixelShifting();

                this.pixelShiftingHandler = setInterval(() => {
                    const range = 12;
                    const top = Math.floor(Math.random() * range) * ((Math.floor(Math.random() * 2) == 0) ? 1 : -1);
                    const left = Math.floor(Math.random() * range) * ((Math.floor(Math.random() * 2) == 0) ? 1 : -1);

                    this.pixelShiftingStyles.top = top === 0 ? 0 : `${top}px`;
                    this.pixelShiftingStyles.left = left === 0 ? 0 : `${left}px`;
                }, 3600000);
            },
            endPixelShifting() {
                this.pixelShiftingStyles.top = 0;
                this.pixelShiftingStyles.left = 0;

                if (this.pixelShiftingHandler) {
                    clearInterval(this.pixelShiftingHandler);
                    this.pixelShiftingHandler = undefined;
                }
            },

            // Fullscreen
            toggleFullscreen() {
                BigScreen.toggle();
            },
            endFullscreen() {
                if (this.isFullscreenActive) {
                    BigScreen.exit();
                    this.isFullscreenActive = false;
                }
            }
		}
	};
</script>


<style lang="scss">
@import '~@/assets/styles/references';

.dashboard-breadcrumbs { text-align: center; position: absolute; top: gx(1); left: 0; right: 0; width: 100%; }
.dashboard-breadcrumb-item { position: relative; display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin: 0 gx(0.25); background-color: color(black, divider); border: 1px solid color(black, divider); cursor: pointer;
    &.active { background-color: color(white, divider); }
    canvas { position: absolute; top: -2px; left: -2px; }
}

.tv-header { padding: gx(1); display: flex;
    h1, h2 { font-family: 'Roboto Condensed'; line-height: 1.2em; margin: 0; padding: 0; }
    h1 { font-size: 24px; font-weight: 700; }
    h2 { font-size: 16px; font-weight: 700; }
    .title { flex: 1 1 auto; }
    .last-updated { flex: 0 0 auto; white-space: nowrap; padding: 0 gx(1); height: 36px; line-height: 36px; font-size: 12px; }
    .close { flex: 0 0 auto; }
}

.btn-outline.btn-tv-close { position: relative; float: right; border: 0; background-color: color(black, divider); color: color(white, divider); }

.toolbar-dashboard { flex-wrap: wrap; width: 100%;
	> ul{ margin-bottom: gx(0.5); }
}

body:fullscreen { background-color: $body-bg; }
html:fullscreen { background-color: $body-bg; }


.fullscreen {
    .appbar,
    .drawer-wrapper { display: none; }
    .app-page { overflow: hidden; }
}

</style>
